import React from 'react';
import { Box, Button, Option, Select } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { AdminStaffAPI } from '../../lib/apiAdmin';

interface StaffPickerProps {
  value: number | null;
  onChange: (userId: number | null) => void;
  placeholder?: string;
  /** Narrow to one function, e.g. 'cs' for a Customer Success owner. */
  functionFilter?: string;
  disabled?: boolean;
  /** Keep the menu inside a containing modal's focus boundary. */
  disablePortal?: boolean;
}

/**
 * One picker for every "who owns this" control.
 *
 * A ticket assignee, a lead owner and a CS owner are the same question, so
 * they share one endpoint and one component rather than three near-copies.
 */
export const StaffPicker: React.FC<StaffPickerProps> = ({
  value,
  onChange,
  placeholder = 'Unassigned',
  functionFilter,
  disabled,
  disablePortal = false,
}) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'assignableStaff'],
    queryFn: () => AdminStaffAPI.assignable(),
    // The staff list barely changes; refetching it per drawer open is waste.
    staleTime: 5 * 60 * 1000,
  });

  const people = (data ?? []).filter(
    (person) => !functionFilter || person.function === functionFilter
  );

  return (
    <Box sx={{minWidth:0}}><Select
      size="sm"
      placeholder={isLoading ? 'Loading…' : placeholder}
      value={value ?? null}
      disabled={disabled || isLoading || !!error}
      slotProps={{ listbox: { disablePortal } }}
      onChange={(event, next) => {
        // Joy fires onChange with a null event when it reconciles the value
        // against the option list rather than because anyone clicked. While
        // the staff list is still loading there is no Option matching the
        // current owner, so that reconciliation arrives as "unassigned" — and
        // every caller here writes straight to the server, which silently
        // cleared the owner each time a drawer was opened. Only a real gesture
        // carries an event.
        if (event === null) return;
        const chosen = (next as number | null) ?? null;
        if (chosen === (value ?? null)) return;
        onChange(chosen);
      }}
    >
      <Option value={null as unknown as number}>{placeholder}</Option>
      {people.map((person) => (
        <Option key={person.id} value={person.id}>
          {person.name}
          {person.function ? ` · ${person.function}` : ''}
        </Option>
      ))}
    </Select>{error && <Button size="sm" variant="plain" color="danger" onClick={()=>refetch()}>Staff unavailable · Retry</Button>}</Box>
  );
};

export default StaffPicker;
